'use client';

import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import { useSetFocusedAsset } from '@frameio/selection';
import type { DownloadAssetsReturn } from '@frameio/shared/assets/types';
import {
  type CopyAssetsReturn,
  type DeleteAssetsReturn,
  type GenerateAssetsReturn,
  type GenerateTranscriptsReturn,
  type MoveAssetsReturn,
  type RenameAssetReturn,
  type ResetAssetLifecycleReturn,
  type RestrictFolderReturn,
  type UbsValidateReturn,
  type UploadCaptionsReturn,
  useCopyAssets,
  useCreateFolder,
  useDeleteAssets,
  useDownloadAssets,
  useGenerateAssets,
  useGenerateTranscripts,
  useMoveAssets,
  useRenameAsset,
  useResetAssetsLifecycle,
  useRestrictFolder,
  useUbsValidate,
  useUploadCaptions,
} from '../hooks';

type AssetOperationsState = {
  foldersWithCreates: Map<string, Set<string>>;
  renameAssetId: string | null;
};

type AssetOperationsValue = {
  setState: Dispatch<SetStateAction<AssetOperationsState>>;
  state: AssetOperationsState;
} & Pick<DeleteAssetsReturn, 'deleteAssets'> &
  Pick<DownloadAssetsReturn, 'downloadAssets' | 'isDownloading'> &
  Pick<RenameAssetReturn, 'isRenameWarningShowing' | 'renameAsset'> &
  Pick<RestrictFolderReturn, 'restrictFolder'> &
  Pick<GenerateAssetsReturn, 'openGenerateAssetsDialog'> &
  Pick<GenerateTranscriptsReturn, 'openGenerateTranscriptsDialog'> &
  Pick<UbsValidateReturn, 'openUbsValidateDialog'> &
  Pick<UploadCaptionsReturn, 'openUploadCaptionsDialog'> &
  Pick<CopyAssetsReturn, 'copyAssets'> &
  Pick<MoveAssetsReturn, 'moveAssets'> &
  Pick<ResetAssetLifecycleReturn, 'resetAssetsLifecycle'>;

const initialState: AssetOperationsState = {
  foldersWithCreates: new Map(),
  renameAssetId: null,
};

export const AssetOperationsContext = createContext<AssetOperationsValue>({
  copyAssets: noop,
  deleteAssets: noop,
  downloadAssets: noop,
  resetAssetsLifecycle: noop,
  isDownloading: false,
  isRenameWarningShowing: false,
  moveAssets: noop,
  openGenerateAssetsDialog: noop,
  openGenerateTranscriptsDialog: noop,
  openUbsValidateDialog: noop,
  openUploadCaptionsDialog: noop,
  renameAsset: noop,
  restrictFolder: noop,
  setState: noop,
  state: initialState,
});

/**
 * The AssetOperationsContextProvider manages state needed to support asset operations.
 * It also mounts any required warning dialogs that are sometimes toggled by different
 * operations.
 */
export function AssetOperationsContextProvider({
  children,
}: PropsWithChildren) {
  const [state, setState] = useState<AssetOperationsState>(initialState);

  const { deleteAssets, deleteWarningDialog } = useDeleteAssets();
  const { openGenerateTranscriptsDialog, generateTranscriptDialog } =
    useGenerateTranscripts();

  const { openUploadCaptionsDialog, uploadCaptionsDialog } =
    useUploadCaptions();

  const { downloadAssets, downloadWarningDialog, isDownloading } =
    useDownloadAssets();

  const { extensionWarningDialog, isRenameWarningShowing, renameAsset } =
    useRenameAsset();

  const { restrictFolder, restrictWarningDialog } = useRestrictFolder();

  const { copyAssets, copyRestrictedFolderDialog, copyCrossProjectDialog } =
    useCopyAssets();

  const { moveAssets, moveRestrictedFolderDialog, moveCrossProjectDialog } =
    useMoveAssets();

  const { resetAssetsLifecycle, confirmResetAssetsLifecycleDialog } =
    useResetAssetsLifecycle();

  const { openGenerateAssetsDialog, generateAssetsDialog } =
    useGenerateAssets();

  const { openUbsValidateDialog, ubsValidateDialog } = useUbsValidate();

  const contextValue = useMemo<AssetOperationsValue>(
    () => ({
      copyAssets,
      deleteAssets,
      downloadAssets,
      isDownloading,
      isRenameWarningShowing,
      moveAssets,
      openGenerateAssetsDialog,
      openGenerateTranscriptsDialog,
      openUbsValidateDialog,
      openUploadCaptionsDialog,
      renameAsset,
      restrictFolder,
      resetAssetsLifecycle,
      setState,
      state,
    }),
    [
      copyAssets,
      deleteAssets,
      downloadAssets,
      isDownloading,
      isRenameWarningShowing,
      moveAssets,
      openGenerateAssetsDialog,
      openGenerateTranscriptsDialog,
      openUbsValidateDialog,
      openUploadCaptionsDialog,
      renameAsset,
      restrictFolder,
      resetAssetsLifecycle,
      state,
    ]
  );

  return (
    <AssetOperationsContext.Provider value={contextValue}>
      <>
        {children}

        {moveRestrictedFolderDialog}
        {moveCrossProjectDialog}
        {copyRestrictedFolderDialog}
        {copyCrossProjectDialog}
        {deleteWarningDialog}
        {downloadWarningDialog}
        {extensionWarningDialog}
        {generateTranscriptDialog}
        {generateAssetsDialog}
        {ubsValidateDialog}
        {uploadCaptionsDialog}
        {restrictWarningDialog}
        {confirmResetAssetsLifecycleDialog}
      </>
    </AssetOperationsContext.Provider>
  );
}

/**
 * useResetAssetsLifecycleWithConfirmation returns a hook that triggers
 * a confirmation dialog before resetting lifecycle.
 */
export function useResetAssetsLifecycleWithConfirmation() {
  return useContextSelector(
    AssetOperationsContext,
    (ctx) => ctx.resetAssetsLifecycle
  );
}

/**
 * useSetRenameAssetId provides a callback for keeping track of which asset name
 * is being inline-edited. It needs to be cleared when the operation is complete.
 *
 * @returns callback Updates or clears the renameAssetId
 */
export function useSetRenameAssetId() {
  const setState = useContextSelector(
    AssetOperationsContext,
    (value) => value.setState
  );

  return useCallback(
    (assetId: string | null) => {
      setState((value) => ({
        ...value,
        renameAssetId: assetId,
      }));
    },
    [setState]
  );
}

/**
 * useRenameAssetWithWarning provides all the callbacks and info needed for a component
 * to submit the asset rename operation.
 *
 * @param id The asset id to rename
 * @param onError Callback fired if rename fails
 * @returns rename.handleRenameSubmit Callback to fire to submit the rename operation
 * @returns rename.isInlineEditing Is the current asset being renamed?
 * @returns rename.isRenameWarningShowing Is the extension warning dialog currently showing?
 * @returns rename.setRenameAssetId Callback for updating or clearing the renameAssetId
 */
export function useRenameAssetWithWarning(id: string, onError = noop) {
  const setRenameAssetId = useSetRenameAssetId();

  const isRenameWarningShowing = useContextSelector(
    AssetOperationsContext,
    (value) => value.isRenameWarningShowing
  );
  const renameAsset = useContextSelector(
    AssetOperationsContext,
    (value) => value.renameAsset
  );
  const renameAssetId = useContextSelector(
    AssetOperationsContext,
    ({ state }) => state.renameAssetId
  );

  const handleRenameSubmit = useCallback(
    (oldName: string, newName: string, onSuccess = noop) => {
      if (oldName.trim() !== newName.trim()) {
        renameAsset({
          assetId: id,
          newName,
          oldName,
          onError,
          onSuccess,
        });
      }

      setRenameAssetId(null);
    },
    [id, onError, renameAsset, setRenameAssetId]
  );

  return {
    handleRenameSubmit,
    isInlineEditing: renameAssetId === id,
    isRenameWarningShowing,
    setRenameAssetId,
  };
}

/**
 * useDeleteAssetsWithWarning provides the deleteAssets callback for deleting assets.
 *
 * @returns callback Callback for deleting assets.
 */
export const useDeleteAssetsWithWarning = () =>
  useContextSelector(AssetOperationsContext, (value) => value.deleteAssets);

/**
 * useDownloadAssetsWithWarning provides the downloadAssets callback for downloading
 * assets as well a boolean for tracking if a download is already in progress.
 *
 * @returns download.downloadAssets Callback for downloading assets.
 * @returns download.isDownloading Whether or not a download is in progress.
 */
export const useDownloadAssetsWithWarning = () =>
  useContextSelector(
    AssetOperationsContext,
    ({ downloadAssets, isDownloading }) => ({
      downloadAssets,
      isDownloading,
    })
  );

/**
 * useCreateAndRenameFolder provides a callback for creating a folder and then
 * selects the new folder and sets it up for renaming.
 *
 * @returns createFolder Callback for creating a folder.
 */
export const useCreateAndRenameFolder = () => {
  const setRenameAssetId = useSetRenameAssetId();
  const setFocusedAssetId = useSetFocusedAsset();

  return useCreateFolder({
    onSuccess(newFolder) {
      if (newFolder?.__typename !== 'FolderAsset') {
        return;
      }

      setFocusedAssetId(newFolder.id);
      setRenameAssetId(newFolder.id);
    },
  });
};

/**
 * useIsCreatingAssetsInFolder returns true if a given folder currently has
 * assets being created during the upload process.
 *
 * @param id The id of the folder
 * @returns isCreating Whether assets are still being created for this folder
 */
export const useIsCreatingAssetsInFolder = (id: string) =>
  useContextSelector(AssetOperationsContext, ({ state }) => {
    const size = state.foldersWithCreates.get(id)?.size;

    return size ? size > 0 : false;
  });

type UpdateFunction = (
  value: AssetOperationsState['foldersWithCreates']
) => AssetOperationsState['foldersWithCreates'];

/**
 * useSetFoldersWithCreates provides a callback for updating the foldersWithCreates
 * state.
 *
 * @returns setFoldersWithCreates Callback for setting a new value for foldersWithCreates
 */
export const useSetFoldersWithCreates = () => {
  const setState = useContextSelector(
    AssetOperationsContext,
    (value) => value.setState
  );

  return useCallback(
    (update: UpdateFunction) => {
      setState((current) => ({
        ...current,
        foldersWithCreates: update(current.foldersWithCreates),
      }));
    },
    [setState]
  );
};

export const useRestrictFolderWithWarning = () =>
  useContextSelector(
    AssetOperationsContext,
    ({ restrictFolder }) => restrictFolder
  );

export const useOpenGenerateAssetsDialog = () =>
  useContextSelector(
    AssetOperationsContext,
    ({ openGenerateAssetsDialog }) => openGenerateAssetsDialog
  );

export const useOpenUbsValidateDialog = () =>
  useContextSelector(
    AssetOperationsContext,
    ({ openUbsValidateDialog }) => openUbsValidateDialog
  );

export const useOpenGenerateTranscriptsDialog = () =>
  useContextSelector(
    AssetOperationsContext,
    ({ openGenerateTranscriptsDialog }) => openGenerateTranscriptsDialog
  );

export const useOpenUploadCaptionsDialog = () =>
  useContextSelector(
    AssetOperationsContext,
    ({ openUploadCaptionsDialog }) => openUploadCaptionsDialog
  );

export const useCopyAssetsWithWarning = () =>
  useContextSelector(AssetOperationsContext, ({ copyAssets }) => copyAssets);

export const useMoveAssetsWithWarning = () =>
  useContextSelector(AssetOperationsContext, ({ moveAssets }) => moveAssets);
